import { useEffect, useState } from 'react';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../apiHelper';

/**
 * Current-scope selection for the left-bar selector. The user picks either one
 * facility or a parent org node they hold a role on:
 *  - facility -> stored as {kind:'facility', id}; the axios interceptor sends
 *    X-Facility-Id so the backend narrows every list to that facility.
 *  - org/parent -> stored as {kind:'org', id}; no X-Facility-Id is sent, so the
 *    backend returns the user's full ACL scope (which it already bounds: a pure
 *    org-admin sees no PHI, a dual-role user sees the union of their facilities).
 * Selecting a scope never widens access - it only narrows within what the ACL
 * already allows.
 */
const STORAGE_KEY = 'currentScope';
const EVENT = 'currentScopeChanged';

export type Facility = { id: string; name: string };
export type OrgNode = { id: string; name: string; type: string };
export type Scope = { kind: 'facility' | 'org'; id: string };

export function getCurrentScope(): Scope | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    return s && (s.kind === 'facility' || s.kind === 'org') && s.id ? s : null;
  } catch {
    return null;
  }
}

export function setCurrentScope(scope: Scope | null): void {
  try {
    if (scope) localStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(EVENT));
}

/**
 * Switch the current scope and refresh the app cache.
 * - Cancels in-flight requests so we don't stack duplicate refetches.
 * - Invalidates every cached query except the selector's own data ('me-facilities').
 *   `invalidateQueries` (not `removeQueries`) preserves the previous payload in cache,
 *   so pages using placeholderData: keepPreviousData keep showing the old numbers
 *   instead of a blank loader while the new scope loads.
 */
export function switchScope(qc: QueryClient, next: Scope | null): void {
  setCurrentScope(next);
  qc.cancelQueries();
  qc.invalidateQueries({
    predicate: (q) => {
      const key = Array.isArray(q.queryKey) ? q.queryKey[0] : q.queryKey;
      return key !== 'me-facilities';
    },
  });
}

/** The facility id the interceptor should forward, or null when org-scoped/unset. */
export function getCurrentFacilityId(): string | null {
  const s = getCurrentScope();
  return s && s.kind === 'facility' ? s.id : null;
}

/** The facilities + parent org nodes the signed-in user can scope to. */
export function useMyScopes() {
  return useQuery({
    queryKey: ['me-facilities'],
    queryFn: async (): Promise<{ facilities: Facility[]; orgNodes: OrgNode[] }> => {
      const res = await apiClient.get('/users/me/facilities');
      const data = (res as any)?.data ?? res;
      return {
        facilities: data?.facilities ?? [],
        orgNodes: data?.org_nodes ?? [],
      };
    },
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

/**
 * Facility/org selector state. The selector renders when the user has more than
 * one scope to switch between (facilities + parent org nodes combined). Changing
 * the scope invalidates cached queries so every page refetches under the new scope.
 */
export function useCurrentFacility() {
  const { data, isLoading } = useMyScopes();
  const facilities = data?.facilities ?? [];
  const orgNodes = data?.orgNodes ?? [];
  const qc = useQueryClient();

  // Re-render whenever setCurrentScope fires from anywhere in the app
  // (e.g. the dashboard map panel), so this hook's consumers stay in sync.
  const [, force] = useState(0);
  useEffect(() => {
    const on = () => force((n) => n + 1);
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);

  const stored = getCurrentScope();
  // Resolve the stored scope; if it's no longer accessible, fall back to the
  // first facility (preserves the prior default for facility-scoped users).
  let scope: Scope | null = null;
  if (stored?.kind === 'org' && orgNodes.some((n) => n.id === stored.id)) {
    scope = stored;
  } else if (stored?.kind === 'facility' && facilities.some((f) => f.id === stored.id)) {
    scope = stored;
  } else if (facilities[0]) {
    scope = { kind: 'facility', id: facilities[0].id };
  }

  const select = (next: Scope | null) => switchScope(qc, next);

  const currentFacility =
    scope?.kind === 'facility' ? facilities.find((f) => f.id === scope!.id) ?? null : null;
  const currentOrg =
    scope?.kind === 'org' ? orgNodes.find((n) => n.id === scope!.id) ?? null : null;

  return {
    facilities,
    orgNodes,
    scope,
    current: currentFacility,
    currentOrg,
    // Show the selector whenever there is more than one place to switch to.
    isMulti: facilities.length + orgNodes.length > 1,
    isLoading,
    selectFacility: (id: string) => select({ kind: 'facility', id }),
    selectOrg: (id: string) => select({ kind: 'org', id }),
  };
}
